import { analyticsLogger } from '@commander/logger';
import type { EventName, EventProperties } from './events';
import { MixpanelProvider } from './providers/mixpanel';
import type { AnalyticsConfig, AnalyticsProvider } from './types';

/**
 * Singleton analytics instance
 */
class Analytics {
  private provider: AnalyticsProvider;
  private isInitialized = false;

  constructor() {
    // Default to Mixpanel provider
    this.provider = new MixpanelProvider();
  }

  /**
   * Initialize the analytics provider with configuration
   * Guard against double initialization by using a global flag that persists across
   * micro-frontend bundles (each micro-app has its own module copy, so we need
   * to coordinate via window/global scope).
   */
  init(config: AnalyticsConfig): void {
    if (this.isInitialized) {
      analyticsLogger.debug('Analytics already initialized, skipping');
      return;
    }

    // Check global flag to prevent initialization from another micro-frontend bundle
    // that shares the same browser context (e.g., shell + driver-routing)
    if (
      typeof window !== 'undefined' &&
      (window as Window & { __analyticsInitialized?: boolean }).__analyticsInitialized
    ) {
      analyticsLogger.debug('Analytics already initialized by another bundle, skipping');
      this.isInitialized = true;
      return;
    }

    this.provider.init(config);
    this.isInitialized = true;

    // Set global flag so other micro-app bundles know analytics is initialized
    if (typeof window !== 'undefined') {
      (window as Window & { __analyticsInitialized?: boolean }).__analyticsInitialized = true;
    }
  }

  /**
   * Track an event with type-safe event name and properties
   */
  track<T extends EventName>(eventName: T, properties?: EventProperties<T>): void {
    if (!this.isInitialized) {
      analyticsLogger.warn('Analytics not initialized. Call init() first.');
      return;
    }

    this.provider.track(eventName as string, properties as Record<string, unknown>);
  }

  /**
   * Identify a user
   */
  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      analyticsLogger.warn('Analytics not initialized. Call init() first.');
      return;
    }

    this.provider.identify(userId, traits);
  }

  /**
   * Reset analytics state (e.g., on logout)
   */
  reset(): void {
    this.provider.reset();
  }

  /**
   * Set a custom analytics provider (for testing or swapping providers)
   */
  setProvider(provider: AnalyticsProvider): void {
    this.provider = provider;
  }
}

/**
 * Singleton analytics instance
 */
export const analytics = new Analytics();

/**
 * Initialize analytics with environment-based configuration
 */
export const initAnalytics = (envConfig?: {
  VITE_MIXPANEL_TOKEN?: string;
  VITE_MIXPANEL_API_HOST?: string;
  VITE_ANALYTICS_ENABLED?: string;
  VITE_ANALYTICS_DEBUG?: string;
}): void => {
  const config: AnalyticsConfig = {
    token: envConfig?.VITE_MIXPANEL_TOKEN,
    apiHost: envConfig?.VITE_MIXPANEL_API_HOST,
    disabled: envConfig?.VITE_ANALYTICS_ENABLED === 'false',
    debug: envConfig?.VITE_ANALYTICS_DEBUG === 'true',
  };

  analytics.init(config);
};
